import type { DriveStep } from 'driver.js';

export const LEADS_HUB_TOUR_SELECTORS = {
  sidebar: '[data-tour="leads-sidebar"]',
  search: '[data-tour="leads-search"]',
  stats: '[data-tour="leads-stats"]',
  table: '[data-tour="leads-table"]',
  quickSearchBtn: '[data-tour="leads-quick-search-btn"]',
  newBtn: '[data-tour="leads-new-btn"]',
  helpBtn: '[data-tour="leads-help-btn"]',
} as const;

export function buildLeadsHubTourSteps(): DriveStep[] {
  const steps: DriveStep[] = [
    {
      popover: {
        title: 'Welcome to Leads',
        description:
          'This is your list-first CRM hub. Filter, search, and open any lead to manage their client portal and communications.',
      },
    },
  ];

  const optional: { selector: string; step: DriveStep }[] = [
    {
      selector: LEADS_HUB_TOUR_SELECTORS.sidebar,
      step: {
        element: LEADS_HUB_TOUR_SELECTORS.sidebar,
        popover: {
          title: 'Filter by status',
          description:
            'Use the sidebar to jump between New, Contacted, Qualified, and Hot leads. Adjust date range and source filters below the status list.',
          side: 'right',
          align: 'start',
        },
      },
    },
    {
      selector: LEADS_HUB_TOUR_SELECTORS.search,
      step: {
        element: LEADS_HUB_TOUR_SELECTORS.search,
        popover: {
          title: 'Filter the list',
          description: 'Narrow the table by name, email, phone, company, location, or labels.',
          side: 'bottom',
          align: 'start',
        },
      },
    },
    {
      selector: LEADS_HUB_TOUR_SELECTORS.stats,
      step: {
        element: LEADS_HUB_TOUR_SELECTORS.stats,
        popover: {
          title: 'At-a-glance stats',
          description: 'Quick counts for total leads, new inquiries, follow-ups due, and qualified prospects.',
          side: 'bottom',
          align: 'start',
        },
      },
    },
    {
      selector: LEADS_HUB_TOUR_SELECTORS.table,
      step: {
        element: LEADS_HUB_TOUR_SELECTORS.table,
        popover: {
          title: 'Lead table',
          description:
            'Click any row to open the client workspace. Last Updated reflects comments, documents, projects, and activity. Select rows for bulk actions.',
          side: 'top',
          align: 'start',
        },
      },
    },
    {
      selector: LEADS_HUB_TOUR_SELECTORS.quickSearchBtn,
      step: {
        element: LEADS_HUB_TOUR_SELECTORS.quickSearchBtn,
        popover: {
          title: 'Quick search',
          description: 'Press Space anywhere on this page (outside a text field) to jump to any lead instantly — like Spotlight.',
          side: 'bottom',
          align: 'end',
        },
      },
    },
    {
      selector: LEADS_HUB_TOUR_SELECTORS.newBtn,
      step: {
        element: LEADS_HUB_TOUR_SELECTORS.newBtn,
        popover: {
          title: 'Add a lead',
          description: 'Quick-add a new lead without leaving this page.',
          side: 'bottom',
          align: 'end',
        },
      },
    },
    {
      selector: LEADS_HUB_TOUR_SELECTORS.helpBtn,
      step: {
        element: LEADS_HUB_TOUR_SELECTORS.helpBtn,
        popover: {
          title: 'Help anytime',
          description: 'Reopen this guide or replay the walkthrough whenever you need a refresher.',
          side: 'bottom',
          align: 'end',
        },
      },
    },
  ];

  for (const item of optional) {
    if (typeof document !== 'undefined' && document.querySelector(item.selector)) {
      steps.push(item.step);
    }
  }

  return steps;
}

export const LEADS_HUB_TOUR_STORAGE_KEY = 'lctf:leadsHubTour:v1';

export function markLeadsHubTourSeen(): void {
  try {
    localStorage.setItem(LEADS_HUB_TOUR_STORAGE_KEY, new Date().toISOString());
  } catch {
    /* ignore */
  }
}

export function hasSeenLeadsHubTour(): boolean {
  try {
    return !!localStorage.getItem(LEADS_HUB_TOUR_STORAGE_KEY);
  } catch {
    return false;
  }
}
